import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TorusAnimation from './TorusAnimation';
import { WebRTCService } from '../services/webrtc';
import { getRoom } from '../services/signaling';
import './SelflessMode.css';

const GESTURE_THRESHOLD = 40;
const MAX_TAP_DISTANCE = 15;

/**
 * SelflessCall - Composant d'appel P2P avec Torus animé par la voix
 *
 * Ce composant gère à la fois :
 * - La connexion WebRTC (signaling via Firebase)
 * - L'analyse audio en temps réel pour animer le Torus
 * - L'interface gestuelle (tap = mute, swipe = actions)
 */
const SelflessCall = ({ roomId, onClose }) => {
  const navigate = useNavigate();

  // États WebRTC
  const [status, setStatus] = useState('initializing'); // initializing, connecting, connected, failed
  const [peers, setPeers] = useState([]);
  const [localStream, setLocalStream] = useState(null);

  // États UI
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoBlack, setIsVideoBlack] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [fadeOutOpacity, setFadeOutOpacity] = useState(0);

  // État Audio Analysis
  const [liveFrequency, setLiveFrequency] = useState(null);
  const [selflesMetrics, setSelflesMetrics] = useState({
    volume: 0,
    volumeDB: 0,
    syllablesPerSec: 0,
    pitchStdDev: 0,
    isPaused: true
  });

  // Refs
  const webrtcRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const analysisIntervalRef = useRef(null);
  const gestureStart = useRef({ x: 0, y: 0, t: 0 });
  const lastTapRef = useRef(0);
  const rafRef = useRef(null);
  const videoElsRef = useRef([]);
  const fileInputImageRef = useRef(null);
  const fileInputAttachmentRef = useRef(null);

  // Buffers pour l'analyse
  const frequencyBufferRef = useRef([]);
  const silenceTimeoutRef = useRef(null);
  const lastAnalysisTimeRef = useRef(0);

  /**
   * Analyse audio en temps réel - Détection de pitch et métriques vocales
   */
  const analyzeLiveAudio = useCallback(() => {
    if (!analyserRef.current || !audioContextRef.current) return;

    const now = Date.now();
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Float32Array(bufferLength);
    analyserRef.current.getFloatFrequencyData(dataArray);

    const sampleRate = audioContextRef.current.sampleRate;
    const fftSize = analyserRef.current.fftSize;
    const frequencyResolution = sampleRate / fftSize;

    // Plage de fréquences vocales
    const minFreq = 60;
    const maxFreq = 1000;
    const startIndex = Math.floor(minFreq * fftSize / sampleRate);
    const endIndex = Math.ceil(maxFreq * fftSize / sampleRate);

    // Trouver les pics dans le spectre
    let peaks = [];
    const noiseThreshold = -60;

    for (let i = startIndex + 2; i < endIndex - 2; i++) {
      const freq = i * frequencyResolution;
      const amplitude = dataArray[i];

      if (amplitude > noiseThreshold &&
          amplitude > dataArray[i-2] &&
          amplitude > dataArray[i-1] &&
          amplitude > dataArray[i+1] &&
          amplitude > dataArray[i+2]) {

        // Interpolation parabolique pour précision
        const alpha = dataArray[i-1];
        const beta = dataArray[i];
        const gamma = dataArray[i+1];
        const p = 0.5 * (alpha - gamma) / (alpha - 2*beta + gamma);
        const interpolatedFreq = freq + p * frequencyResolution;

        peaks.push({
          frequency: interpolatedFreq,
          amplitude: beta
        });
      }
    }

    // Trier par amplitude
    peaks.sort((a, b) => b.amplitude - a.amplitude);

    // Sélectionner la fréquence fondamentale
    let detectedFrequency = peaks.length > 0 ? peaks[0].frequency : null;

    // Calculer le volume RMS
    const timeData = new Float32Array(fftSize);
    analyserRef.current.getFloatTimeDomainData(timeData);
    let rms = 0;
    for (let i = 0; i < timeData.length; i++) {
      rms += timeData[i] * timeData[i];
    }
    rms = Math.sqrt(rms / timeData.length);
    const volumeDB = 20 * Math.log10(Math.max(rms, 0.0001));

    if (detectedFrequency === null || volumeDB < -50) {
      // Silence détecté
      if (silenceTimeoutRef.current === null) {
        silenceTimeoutRef.current = setTimeout(() => {
          setLiveFrequency(null);
          setSelflesMetrics(prev => ({ ...prev, isPaused: true }));
          frequencyBufferRef.current = [];
          silenceTimeoutRef.current = null;
        }, 500);
      }
      return;
    }

    // Clear silence timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    // Ajouter au buffer pour le lissage
    frequencyBufferRef.current.push(detectedFrequency);
    if (frequencyBufferRef.current.length > 5) {
      frequencyBufferRef.current.shift();
    }

    // Moyenne lissée
    const avgFreq = frequencyBufferRef.current.reduce((a, b) => a + b, 0) / frequencyBufferRef.current.length;

    // Calcul de la variation de pitch (écart-type)
    let pitchStdDev = 0;
    if (frequencyBufferRef.current.length > 1) {
      const variance = frequencyBufferRef.current.reduce((sum, f) => sum + Math.pow(f - avgFreq, 2), 0) / frequencyBufferRef.current.length;
      pitchStdDev = Math.sqrt(variance);
    }

    // Mise à jour des états
    setLiveFrequency(Math.round(avgFreq));
    setSelflesMetrics({
      volume: Math.min(1, rms * 10),
      volumeDB: Math.max(-60, volumeDB),
      syllablesPerSec: 0, // TODO: implémenter détection syllabes
      pitchStdDev: pitchStdDev,
      isPaused: false
    });

    lastAnalysisTimeRef.current = now;
  }, []);

  /**
   * Initialisation WebRTC + Audio
   */
  useEffect(() => {
    if (!roomId) {
      console.error('No room ID provided');
      setStatus('failed');
      return;
    }

    const initCall = async () => {
      try {
        setStatus('initializing');

        // 1. Obtenir le stream local (Audio + Video)
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: 48000
          },
          video: { facingMode: 'user', width: 1280, height: 720 }
        });
        setLocalStream(stream);

        // 2. Configurer l'analyse audio
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.3;
        analyser.minDecibels = -100;
        analyser.maxDecibels = -10;
        analyserRef.current = analyser;

        // Connecter l'audio stream à l'analyseur
        const audioTrack = stream.getAudioTracks()[0];
        const audioStream = new MediaStream([audioTrack]);
        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);

        // Démarrer l'analyse en temps réel
        analysisIntervalRef.current = setInterval(analyzeLiveAudio, 30);

        // 3. Initialiser WebRTC
        const webrtc = new WebRTCService();
        webrtcRef.current = webrtc;

        await webrtc.init(stream, (remoteStream) => {
          setPeers([{ id: 'remote', stream: remoteStream }]);
          setStatus('connected');
        });

        // 4. Créer ou rejoindre la room
        const roomData = await getRoom(roomId);

        if (roomData && roomData.offer && !roomData.answer) {
          console.log("Joining existing room...");
          setStatus('connecting');
          await webrtc.joinCall(roomId);
        } else if (!roomData || !roomData.offer) {
          console.log("Creating new room...");
          setStatus('connecting');
          await webrtc.createCall(roomId);
        } else {
          console.log("Room is full");
          setStatus('failed');
        }
      } catch (err) {
        console.error("Error initializing call:", err);
        setStatus('failed');
      }
    };

    initCall();

    // Cleanup
    return () => {
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (webrtcRef.current) {
        webrtcRef.current.hangup();
      }
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [roomId, analyzeLiveAudio]);

  // Sync video elements
  useEffect(() => {
    videoElsRef.current = Array.from(document.querySelectorAll('.peer-video'));
  }, [peers]);

  // Cleanup RAF
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  /**
   * Gestion du Hangup avec fade
   */
  const handleHangupFade = () => {
    const start = performance.now();
    const duration = 1600;

    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setFadeOutOpacity(p);

      videoElsRef.current.forEach((v) => {
        try {
          v.volume = Math.max(0, 1 - p);
        } catch (_) {}
      });

      if (p < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        // Cleanup
        if (analysisIntervalRef.current) {
          clearInterval(analysisIntervalRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (webrtcRef.current) {
          webrtcRef.current.hangup();
        }
        if (localStream) {
          localStream.getTracks().forEach(track => track.stop());
        }

        if (onClose) {
          onClose();
        } else {
          navigate('/');
        }
      }
    };

    rafRef.current = requestAnimationFrame(step);
  };

  /**
   * Toggle Mute - CORRIGÉ
   */
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);

    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !newMuted;
      }
    }
  };

  /**
   * Toggle Black Screen - CORRIGÉ
   */
  const toggleBlackScreen = () => {
    const newBlack = !isVideoBlack;
    setIsVideoBlack(newBlack);

    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !newBlack;
      }
    }
  };

  // Overlay controls
  const openShareMenu = () => {
    setShowContacts(false);
    setShowShareMenu(true);
  };

  const openContacts = () => {
    setShowShareMenu(false);
    setShowContacts(true);
  };

  const closeOverlays = () => {
    setShowShareMenu(false);
    setShowContacts(false);
  };

  // Gesture handling
  const onPointerDown = (clientX, clientY) => {
    gestureStart.current = { x: clientX, y: clientY, t: Date.now() };
  };

  const onPointerUp = (clientX, clientY) => {
    const dx = clientX - gestureStart.current.x;
    const dy = clientY - gestureStart.current.y;
    const dist = Math.hypot(dx, dy);

    const now = Date.now();

    // Double-tap to exit black screen
    if (isVideoBlack && dist <= MAX_TAP_DISTANCE) {
      if (now - lastTapRef.current < 350) {
        setIsVideoBlack(false);
        if (localStream) {
          const videoTrack = localStream.getVideoTracks()[0];
          if (videoTrack) videoTrack.enabled = true;
        }
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      return;
    }

    if (dist <= MAX_TAP_DISTANCE) {
      toggleMute();
      return;
    }

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > GESTURE_THRESHOLD) {
      if (dx < 0) {
        toggleBlackScreen();
      } else {
        handleHangupFade();
      }
      return;
    }

    if (Math.abs(dy) > GESTURE_THRESHOLD) {
      if (dy < 0) {
        openShareMenu();
      } else {
        openContacts();
      }
    }
  };

  const handleMouseDown = (e) => onPointerDown(e.clientX, e.clientY);
  const handleMouseUp = (e) => onPointerUp(e.clientX, e.clientY);
  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    onPointerDown(e.touches[0].clientX, e.touches[0].clientY);
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (t) onPointerUp(t.clientX, t.clientY);
  };

  // Share actions
  const handleShareText = async () => {
    const text = `Join my Selfless call: ${window.location.href}`;
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch (_) {}
    } else {
      navigator.clipboard.writeText(text);
      alert('Link copied to clipboard!');
    }
    setShowShareMenu(false);
  };

  const handleShareImage = () => {
    fileInputImageRef.current?.click();
  };

  const handleShareAttachment = () => {
    fileInputAttachmentRef.current?.click();
  };

  // Loading states
  if (status === 'initializing' || status === 'connecting') {
    return (
      <div className="selfless-mode">
        <div className="media-stage">
          <div className="waiting-peers">
            <p>{status === 'initializing' ? 'Initializing camera and microphone...' : 'Connecting to call...'}</p>
            <div className="torus-overlay" style={{ width: '200px', height: '200px', bottom: '50%', transform: 'translate(-50%, 50%)' }}>
              <TorusAnimation
                disablePopup={true}
                isSelflesMode={true}
                liveFrequency={liveFrequency}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="selfless-mode">
        <div className="media-stage">
          <div className="waiting-peers">
            <p>Failed to connect to call.</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '20px', padding: '10px 20px' }}>
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="selfless-mode">
      <div className="media-stage">
        {/* Remote peers grid */}
        <div className="video-grid">
          {peers.length === 0 ? (
            <div className="waiting-peers">
              <p>Waiting for someone to join...</p>
              <p className="room-link" style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '10px' }}>
                Share this link: {window.location.href}
              </p>
            </div>
          ) : (
            peers.map((peer) => (
              <video
                key={peer.id}
                className="peer-video"
                autoPlay
                playsInline
                ref={(el) => {
                  if (el && peer.stream) {
                    try { el.srcObject = peer.stream; } catch (_) {}
                  }
                }}
              />
            ))
          )}
        </div>

        {/* Torus with LIVE frequency */}
        <div className={`torus-overlay ${isMuted ? 'muted' : ''}`}>
          <TorusAnimation
            disablePopup={true}
            isSelflesMode={true}
            liveFrequency={liveFrequency}
            selflesMetrics={selflesMetrics}
          />
        </div>

        {/* Share menu */}
        {showShareMenu && (
          <div className="torus-share-anchor">
            <div className="share-menu" onClick={closeOverlays}>
              <div className="share-option share-text" onClick={(e) => { e.stopPropagation(); handleShareText(); }} title="Share link">
                🔗
              </div>
              <div className="share-option share-image" onClick={(e) => { e.stopPropagation(); handleShareImage(); }} title="Share image">
                🖼️
              </div>
              <div className="share-option share-attach" onClick={(e) => { e.stopPropagation(); handleShareAttachment(); }} title="Share file">
                📎
              </div>
              <input type="file" accept="image/*" ref={fileInputImageRef} style={{ display: 'none' }} onChange={() => setShowShareMenu(false)} />
              <input type="file" ref={fileInputAttachmentRef} style={{ display: 'none' }} onChange={() => setShowShareMenu(false)} />
            </div>
          </div>
        )}

        {/* Gesture layer */}
        <div
          className="gesture-layer"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => e.preventDefault()}
        />

        {/* Contacts panel */}
        {showContacts && (
          <div className="contacts-panel" role="dialog" aria-modal="true">
            <div className="contacts-header">
              <span>Contacts</span>
              <button className="contacts-close" onClick={() => setShowContacts(false)} aria-label="Close">✕</button>
            </div>
            <div className="contacts-body">
              <div className="contacts-empty">No contacts available</div>
            </div>
          </div>
        )}

        {/* Black screen overlay */}
        {isVideoBlack && <div className="black-overlay" />}

        {/* Hangup fade */}
        {fadeOutOpacity > 0 && (
          <div className="hangup-fade" style={{ opacity: fadeOutOpacity }} />
        )}

        {/* Status bar */}
        <div className="selfless-status-bar" style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          background: 'rgba(0,0,0,0.6)',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          color: 'white',
          zIndex: 10
        }}>
          {isMuted && <span style={{ color: '#ff4444' }}>🔇 Muted</span>}
          {liveFrequency && <span>{liveFrequency} Hz</span>}
          <span style={{ opacity: 0.6 }}>Room: {roomId}</span>
        </div>
      </div>
    </div>
  );
};

export default SelflessCall;
